import { useId, type InputHTMLAttributes, type ReactNode, type Ref } from 'react'

import { cn } from '@/lib/cn'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  /** Inline validation message. Replaces the hint while it is present. */
  error?: string
  hint?: string
  /** Rendered inside the field on the left, e.g. a search icon. */
  leading?: ReactNode
  /** Rendered inside the field on the right, e.g. a clear button. */
  trailing?: ReactNode
  containerClassName?: string
  ref?: Ref<HTMLInputElement>
}

export function Input({
  label,
  error,
  hint,
  leading,
  trailing,
  className,
  containerClassName,
  id,
  type = 'text',
  ...props
}: InputProps) {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const errorId = `${inputId}-error`
  const hintId = `${inputId}-hint`
  const describedBy = error ? errorId : hint ? hintId : undefined

  return (
    <div className={cn('flex flex-col gap-1.5', containerClassName)}>
      {label ? (
        <label htmlFor={inputId} className="text-sm font-medium text-text">
          {label}
        </label>
      ) : null}

      <div className="relative flex items-center">
        {leading ? (
          <span
            className="pointer-events-none absolute left-3 flex text-text-subtle"
            aria-hidden="true"
          >
            {leading}
          </span>
        ) : null}

        <input
          id={inputId}
          type={type}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(
            'h-11 w-full rounded-lg border border-border bg-surface-raised px-3 text-base text-text',
            'placeholder:text-text-subtle',
            'transition-colors hover:border-border-strong',
            'disabled:cursor-not-allowed disabled:opacity-60',
            leading && 'pl-10',
            trailing && 'pr-11',
            error && 'border-danger',
            className,
          )}
          {...props}
        />

        {trailing ? <span className="absolute right-1 flex items-center">{trailing}</span> : null}
      </div>

      {error ? (
        <p id={errorId} role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-sm text-text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  )
}
